import React from 'react';
import { ChevronDownIcon, MenuButtonIcon, SendIcon } from '../icons/TourIcons';
import styles from '../stylesheets/invoiceTransactionContainer.module.scss';
import classNames from 'classnames';
import { AnchorId } from '../core/Enums';
import type { PaymentsInvoice, PaymentsLineItem } from '../core/Types';
import { useTourAnchor } from '../hooks/useTourAnchor';

export type InvoiceLineItem = PaymentsLineItem;
export type InvoiceData = PaymentsInvoice;

interface InvoiceTransactionContainerProps {
  invoice: InvoiceData;
  actionLabel?: string;
  onAction?: () => void;
  defaultExpanded?: boolean;
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(value);

interface InvoiceHeaderProps {
  issuedDate: string;
  dueDate: string;
  expanded: boolean;
  onToggle: () => void;
}

export const InvoiceHeader = ({
  issuedDate,
  dueDate,
  expanded,
  onToggle,
}: InvoiceHeaderProps) => {
  return (
    <div className={styles.headerBlock}>
      <div
        className={styles.headerRow}
        role="button"
        tabIndex={0}
        onClick={onToggle}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onToggle();
          }
        }}
        aria-expanded={expanded}
      >
        <div className={styles.headerButton}>
          <span
            className={
              expanded ? styles.chevronExpanded : styles.chevronCollapsed
            }
          >
            <ChevronDownIcon />
          </span>
        </div>
        <div className={styles.headerContent}>
          <div className={styles.headerMeta}>
            <span className={styles.headerTitle}>Outstanding Rent</span>
            <div className={styles.datesRowDesktop}>
              <div className={styles.dateCard}>
                <span className={styles.dateLabel}>Issued</span>
                <div className={styles.dateValueRow}>
                  <span className={styles.dateValue}>{issuedDate}</span>
                </div>
              </div>
              <div className={styles.dateCard}>
                <span className={styles.dateLabel}>Due</span>
                <div className={styles.dateValueRow}>
                  <span className={styles.dateValue}>{dueDate}</span>
                </div>
              </div>
            </div>
          </div>
          <div className={styles.datesRowMobile}>
            <div className={styles.dateCard}>
              <span className={styles.dateLabel}>Issued</span>
              <div className={styles.dateValueRow}>
                <span className={styles.dateValue}>{issuedDate}</span>
              </div>
            </div>
            <div className={styles.dateCard}>
              <span className={styles.dateLabel}>Due</span>
              <div className={styles.dateValueRow}>
                <span className={styles.dateValue}>{dueDate}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

interface InvoiceLineItemsProps {
  lineItems: InvoiceLineItem[];
  total: number;
}

export const InvoiceLineItems = ({
  lineItems,
  total,
}: InvoiceLineItemsProps) => {
  if (lineItems.length === 0) {
    return null;
  }

  return (
    <div className={styles.lineItemsSection}>
      <div className={styles.tableHeader}>
        <div>Item</div>
        <div>Site</div>
        <div>Description</div>
        <div>Company</div>
        <div className={styles.alignRight}>Qty.</div>
        <div className={styles.alignRight}>Amount USD</div>
      </div>
      {lineItems.map((item) => {
        const lineTotal = item.amount * item.quantity;
        return (
          <div key={item.id} className={styles.lineItemGroup}>
            <div className={styles.tableRow}>
              <div>{item.itemName}</div>
              <div>{item.site || '-'}</div>
              <div>{item.description || '-'}</div>
              <div>{item.company || '-'}</div>
              <div className={styles.alignRight}>{item.quantity}</div>
              <div className={styles.alignRight}>
                {formatCurrency(item.amount).replace('$', '')}
              </div>
            </div>
            <div className={styles.mobileRow}>
              <div className={styles.lineItemLeft}>
                <div className={styles.itemName}>{item.itemName}</div>
                <div className={styles.itemSite}>{item.site || '-'}</div>
                <div className={styles.itemMeta}>{item.company || '-'}</div>
                <div className={styles.itemMeta}>{item.description || '-'}</div>
              </div>
              <div className={styles.lineItemRight}>
                <div className={styles.lineAmount}>
                  {formatCurrency(lineTotal)}
                </div>
                <div className={styles.lineQuantity}>
                  {item.quantity} x {formatCurrency(item.amount)}
                </div>
              </div>
            </div>
            <div className={styles.rowDivider} />
          </div>
        );
      })}
      <div className={styles.totalRow}>
        <span className={styles.totalLabel}>Total:</span>
        <span className={styles.totalValue}>
          {formatCurrency(total).replace('$', '')}
        </span>
      </div>
    </div>
  );
};

interface InvoiceSummaryProps {
  total: number;
  balance: number;
}

export const InvoiceSummary = ({ total, balance }: InvoiceSummaryProps) => {
  const balanceAmountDueRef = useTourAnchor(AnchorId.BALANCE_AMOUNT_DUE);

  return (
    <div className={styles.summarySection}>
      <div className={styles.summaryLeft}>
        <div className={styles.summaryRow}>
          <span className={styles.summaryLabel}>Original:</span>
          <span className={styles.summaryValue}>{formatCurrency(total)}</span>
        </div>
        <div className={styles.summaryRow}>
          <span className={styles.summaryLabel}>Outstanding:</span>
          <span
            ref={balanceAmountDueRef}
            data-tour={AnchorId.BALANCE_AMOUNT_DUE}
            className={styles.summaryValue}
          >
            {formatCurrency(balance)}
          </span>
        </div>
      </div>
      <div className={styles.summaryRight}>
        <span className={styles.amountLabel}>Amount to pay</span>
        <span className={styles.amountValue}>{formatCurrency(balance)}</span>
      </div>
    </div>
  );
};

const InvoiceTransactionContainer = ({
  invoice,
  defaultExpanded = false,
}: InvoiceTransactionContainerProps) => {
  const [expanded, setExpanded] = React.useState(defaultExpanded);

  return (
    <section className={styles.container}>
      <InvoiceHeader
        issuedDate={invoice.issuedDate}
        dueDate={invoice.dueDate}
        expanded={expanded}
        onToggle={() => setExpanded((prev) => !prev)}
      />
      {expanded && (
        <InvoiceLineItems lineItems={invoice.lineItems} total={invoice.total} />
      )}
      <InvoiceSummary total={invoice.total} balance={invoice.balance} />
    </section>
  );
};

export default InvoiceTransactionContainer;
